import {
  Dispatch,
  FormEvent,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import Button from "@vfuk/core-button";
import { Checkbox } from "@vfuk/core-checkbox";
import { GridRow } from "@vfuk/core-grid";
import Heading from "@vfuk/core-heading";
import Paragraph from "@vfuk/core-paragraph";
import SimpleGrid from "@vfuk/core-simple-grid";
import Spacing from "@vfuk/core-spacing";
import StateNotification from "@vfuk/core-state-notification";

import { SelectInput, SelectOption, Stack } from "@ucchub/ui";
import { CustomerCommercialCriteriaAddRequestV1 } from "@ucchub/ui/src/apiTypes/cloudNumbers";
import useNumberType from "@ucchub/ui/src/cloudNumbers/hooks/numberType/useNumberType";

import { Country } from "@components/country/Country.types";
import useBillingProfiles from "@hooks/billingProfiles/useBillingProfiles";
import useServiceType from "@hooks/serviceType/useServiceType";
import useUseCase from "@hooks/useCase/useUseCase";

import BillingProfileTable from "./BillingProfileTable";
import commercialCriteriaFormValue from "./commercialCriteria.json";

interface AddCommercialCriteriaFormProps {
  error: string | undefined;
  customerId: string;
  countryDetails: Country[];
  activeStep: number;
  handleActiveStep: (step: number) => void;
  formValues: AddCommercialCriteriaFormValues;
  setFormValues: Dispatch<SetStateAction<AddCommercialCriteriaFormValues>>;
  onSubmit: () => void;
  closeModal: () => void;
}

export interface AddCommercialCriteriaFormValues
  extends CustomerCommercialCriteriaAddRequestV1 {
  isCheckboxChecked?: boolean;
}

const AddCommercialCriteriaForm = ({
  error,
  customerId,
  countryDetails,
  activeStep,
  handleActiveStep,
  formValues,
  setFormValues,
  onSubmit,
  closeModal,
}: AddCommercialCriteriaFormProps) => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<SelectOption>();
  const [selectedNumberTypeID, setSelectedNumberType] =
    useState<SelectOption>();
  const [selectedServiceType, setSelectedServiceType] =
    useState<SelectOption>();
  const [selectedUseCase, setSelectedUseCase] = useState<SelectOption>();
  const [selectedBillingProfile, setSelectedBillingProfile] =
    useState<SelectOption>();

  const { fetchNumberTypeList, numberTypeList } = useNumberType();
  const { fetchServiceTypeList, serviceTypeList } = useServiceType();
  const { fetchUseCaseList, useCaseList } = useUseCase();

  const { fetchBillingProfiles, billingProfiles = [] } = useBillingProfiles();

  const handleInputChange = (name: string, value: string | boolean) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const [formErrors, setFormErrors] = useState(0);

  const handleSelectOnChange = (
    input: string,
    selectedOption: SelectOption
  ) => {
    const actions: Record<string, () => void> = {
      countryId: () => {
        setSelectedCountry(selectedOption);
        fetchNumberTypeList({ countryId: selectedOption.value });
        setFormValues({
          ...commercialCriteriaFormValue,
          countryId: selectedOption.value,
        });
        setSelectedNumberType(undefined);
        handleActiveStep(1);
      },
      numberTypeId: () => {
        setSelectedNumberType(selectedOption);
        fetchServiceTypeList({
          countryId: selectedCountry?.value,
          numberTypeId: selectedOption?.value,
        });
        setFormValues({
          ...commercialCriteriaFormValue,
          countryId: formValues.countryId,
          numberTypeId: selectedOption.value,
        });
        setSelectedServiceType(undefined);
        setSelectedUseCase(undefined);
        setSelectedBillingProfile(undefined);
        fetchBillingProfiles(customerId);
        handleActiveStep(2);
      },
      serviceTypeId: () => {
        setSelectedServiceType(selectedOption);
        fetchUseCaseList({
          countryId: selectedCountry?.value,
          numberTypeId: selectedNumberTypeID?.value,
          serviceTypeId: selectedOption?.value,
        });
        setFormValues({
          ...commercialCriteriaFormValue,
          countryId: formValues.countryId,
          numberTypeId: formValues.numberTypeId,
          serviceTypeId: selectedOption.value,
        });
        setSelectedUseCase(undefined);
        setSelectedBillingProfile(undefined);
      },
      useCaseId: () => {
        setSelectedUseCase(selectedOption);
        setFormValues({
          ...commercialCriteriaFormValue,
          countryId: formValues.countryId,
          numberTypeId: formValues.numberTypeId,
          serviceTypeId: formValues.serviceTypeId,
          useCaseId: selectedOption.value,
        });
        setSelectedBillingProfile(undefined);
      },
      billingProfileId: () => {
        setSelectedBillingProfile(selectedOption);
        setFormValues({
          ...formValues,
          billingProfileId: selectedOption.value,
        });
        handleActiveStep(3);
      },
    };
    actions[input]?.();
  };

  const handleValidation = () => {
    let formErrors = 0;
    formErrors += formValues.isCheckboxChecked === false ? 1 : 0;
    formErrors += selectedBillingProfile === undefined ? 1 : 0;
    return formErrors;
  };

  const handleOnSubmit = (data: FormEvent<HTMLFormElement>) => {
    data.preventDefault();
    if (formErrors === 0) {
      onSubmit();
    }
  };

  useEffect(() => {
    setFormErrors(handleValidation());
  }, [formValues]);

  return (
    <>
      {error && (
        <Spacing spacingLevel={{ top: 4 }}>
          <StateNotification state="error">
            <Paragraph>{error}</Paragraph>
          </StateNotification>
        </Spacing>
      )}
      <form onSubmit={handleOnSubmit}>
        <Stack direction="column" align="left">
          <Heading level={5} weight={3} size={2}>
            {t(
              "core:commercialCriteria:add_commercial_criteria.primary_header"
            )}
          </Heading>
          <Paragraph weight={2}>
            {t(
              "core:commercialCriteria:add_commercial_criteria.secondary_header"
            )}
          </Paragraph>
        </Stack>
        <hr />
        {/* ROW 1 */}
        <Spacing spacingLevel={{ top: 4 }}>
          <SimpleGrid
            columns={{
              sm: 1,
              md: 4,
            }}
            spacing={2}
          >
            <SelectInput
              label={t(
                "core:commercialCriteria:add_commercial_criteria.countryId"
              )}
              id="Country"
              value={selectedCountry}
              options={countryDetails?.map((countryId) => ({
                value: countryId.id,
                label: countryId.name,
              }))}
              onChange={(option: SelectOption) => {
                handleSelectOnChange("countryId", option);
              }}
            />
          </SimpleGrid>
        </Spacing>
        {/* ROW 2 */}
        <SimpleGrid
          columns={{
            sm: 1,
            md: 4,
          }}
          spacing={2}
        >
          {activeStep > 0 && (
            <SelectInput
              label={t(
                "core:commercialCriteria:add_commercial_criteria.numberTypeId"
              )}
              id="numberTypeId"
              value={selectedNumberTypeID}
              options={numberTypeList.map((numberTypeId) => ({
                value: numberTypeId.id,
                label: numberTypeId.name,
              }))}
              onChange={(option: SelectOption) => {
                handleSelectOnChange("numberTypeId", option);
              }}
            />
          )}

          {activeStep > 1 && (
            <SelectInput
              label={t(
                "core:commercialCriteria:add_commercial_criteria.serviceTypeId"
              )}
              id="serviceTypeId"
              value={selectedServiceType}
              options={serviceTypeList.map((serviceTypeId) => ({
                value: serviceTypeId.id,
                label: serviceTypeId.name,
              }))}
              onChange={(option: SelectOption) => {
                handleSelectOnChange("serviceTypeId", option);
              }}
            />
          )}
          {activeStep > 1 && (
            <SelectInput
              label={t(
                "core:commercialCriteria:add_commercial_criteria.useCaseId"
              )}
              id="useCaseId"
              value={selectedUseCase}
              options={useCaseList.map((useCaseId) => ({
                value: useCaseId.id,
                label: useCaseId.name,
              }))}
              onChange={(option: SelectOption) => {
                handleSelectOnChange("useCaseId", option);
              }}
            />
          )}
        </SimpleGrid>
        {activeStep > 1 && (
          <SimpleGrid
            columns={{
              sm: 1,
              md: 4,
            }}
            spacing={2}
          >
            <SelectInput
              label={t(
                "core:commercialCriteria:add_commercial_criteria.billingProfileId"
              )}
              id="billingProfileId"
              value={selectedBillingProfile}
              options={billingProfiles.map((billingProfileId) => ({
                value: billingProfileId.id,
                label: billingProfileId.name,
              }))}
              onChange={(option: SelectOption) =>
                handleSelectOnChange("billingProfileId", option)
              }
            />
          </SimpleGrid>
        )}
        {/* ROW 4 */}
        {activeStep > 2 && (
          <>
            <BillingProfileTable
              billingProfileId={selectedBillingProfile?.value}
              billingProfileList={billingProfiles}
              removeBillingProfileHeader={true}
            />
          </>
        )}
        {/* ROW 5 */}
        {activeStep > 1 && (
          <SimpleGrid
            columns={{
              sm: 1,
            }}
            spacing={2}
          >
            <Checkbox
              id="checkbox"
              name="checkbox"
              value=""
              checked={formValues.isCheckboxChecked || false}
              onChange={(option) =>
                handleInputChange("isCheckboxChecked", option.target.checked)
              }
            >
              {t("core:commercialCriteria:add_commercial_criteria.check_box")}
            </Checkbox>
          </SimpleGrid>
        )}
        <hr />
        <Spacing spacingLevel={{ top: 4 }}>
          <GridRow justify="end" marginBottom={3}>
            <Spacing
              spacingLevel={{
                right: 3,
              }}
            >
              <Button
                text={t(
                  "core:commercialCriteria:add_commercial_criteria.cancel"
                )}
                appearance="alt1"
                onClick={closeModal}
              />
            </Spacing>

            <Button
              state={formErrors === 0 ? undefined : "disabled"}
              text={t(
                "core:commercialCriteria:add_commercial_criteria.confirm"
              )}
              onClick={() => null} // Empty `onClick` adds the 'pointer' cursor
              appearance="primary"
              htmlAttributes={{ type: "submit" }}
            />
          </GridRow>
        </Spacing>
      </form>
    </>
  );
};

export default AddCommercialCriteriaForm;
